import { observable, action, runInAction, computed, reaction } from 'mobx';
import _ from 'lodash';
import { message } from 'antd';
import type RulesStore from './RulesStore';
import type TagsGroupVisualStore from './TagsGroupVisualStore';
import type { ErrorStore } from '../../common/stores';
import type { TagModel } from '../models/TagModel';
import type {
    PendingRuleImportAttempt,
    RuleImportCommitTarget,
    RuleImportOrigin,
    RuleImportState,
    RuleImportUploadRequest,
    RulesToUploadData,
    RulesToUploadDataForIotaImport,
    RulesToUploadDataForProjectBindingsImport,
    TagUsageInTestProjects
} from '../types';
import { endpoints } from '../../../config';
import { showImportWarnings } from '../../common/services/ImportWarnings';

// Owns the pending upload, import choices, blocker review and commit lifetime.
// Resolving a Test Project changes that project separately; returning to import preserves the original import choices.
export default class RulesImportVisualStore {
    // A pending write outlives its dialog or Alpha-project selection until the server responds.
    @observable.ref
    committingAttempt: PendingRuleImportAttempt | null = null;

    @observable.ref
    private importState: RuleImportState | null = null;

    private readonly disposeProjectReaction;

    constructor(
        public readonly rulesStore: RulesStore,
        private readonly tagsGroupStore: TagsGroupVisualStore,
        private readonly errorStore: ErrorStore
    ) {
        this.disposeProjectReaction = reaction(
            () => this.rulesStore.currentProject?.id,
            projectId => {
                if (this.importState && this.importState.projectId !== projectId) {
                    this.cancel();
                }
            }
        );
    }

    @computed
    get pendingAttempt() {
        return this.importState?.phase === 'uploading' ? null : this.importState;
    }

    @computed
    get isLoading() {
        return this.committingAttempt !== null;
    }

    @computed
    get isDialogVisible() {
        return !!this.activeAttempt;
    }

    @computed
    get isResolvingTestProjectUsage() {
        return this.activeAttempt?.phase === 'resolution';
    }

    @computed
    get usageStatus() {
        const attempt = this.activeAttempt;

        if (!attempt || this.rulesStore.tagsUsageProjectId !== attempt.projectId) {
            return 'idle';
        }

        return this.rulesStore.tagsUsageStatus;
    }

    @computed
    get importTags() {
        return Object.keys(this.activeAttempt?.importedRules ?? {}).map(name => ({ name }));
    }

    @computed
    get selectedRuleIds(): string[] {
        const attempt = this.activeAttempt;
        if (!attempt) {
            return [];
        }

        if (attempt.target.type === 'rules') {
            return _.union(attempt.selectedRulesKeys, attempt.selectedRulesKeysByTag);
        }

        return _.flatten(Object.values(attempt.importedRules)).map(rule => rule.id);
    }

    @computed
    get isRuleSelected() {
        return this.selectedRuleIds.length > 0;
    }

    @computed
    get allGroupsSelected() {
        const attempt = this.activeAttempt;

        if (!attempt) {
            return false;
        }

        // Field imports allow unassigned groups; application imports require every group.
        if (attempt.target.type === 'fields') {
            return true;
        }

        const tags =
            attempt.target.type === 'applications' ? Object.keys(attempt.tagGroupDict) : this.getSelectedTags(attempt);

        return tags.every(tag => attempt.tagGroupDict[tag] != null);
    }

    @computed
    get canAdvanceImport() {
        const attempt = this.activeAttempt;

        if (!attempt) {
            return false;
        }

        if (this.isLoading || this.usageStatus !== 'ready') {
            return false;
        }

        if (attempt.scope === 'full') {
            return true;
        }

        if (!this.allGroupsSelected) {
            return false;
        }

        // Application and field imports include their own rules without manual rule selection.
        return attempt.target.type !== 'rules' || this.isRuleSelected;
    }

    @computed
    get replacedTagsUsedInTestProjects(): TagUsageInTestProjects[] {
        const attempt = this.activeAttempt;

        if (!attempt) {
            return [];
        }

        if (attempt.scope === 'full') {
            return this.rulesStore.tagsUsageInTestProjects.filter(usage => usage.testProjects.length > 0);
        }

        const replacedTagIds = this.rulesStore.ruleTags
            .filter(tag => attempt.tagActionDict[tag.name] === 'Replace')
            .map(tag => tag.id);

        return this.rulesStore.tagsUsageInTestProjects.filter(
            usage => replacedTagIds.includes(usage.tagId) && usage.testProjects.length > 0
        );
    }

    @computed
    get affectedTestProjects() {
        const projects = new Map<string, { testProjectId: string; testProjectName: string }>();

        for (const usage of this.replacedTagsUsedInTestProjects) {
            for (const project of usage.testProjects) {
                projects.set(project.testProjectId, project);
            }
        }

        return Array.from(projects.values());
    }

    @action.bound
    begin(origin: RuleImportOrigin, uploadId: string, onImported: () => Promise<void> = () => Promise.resolve()) {
        const projectId = this.rulesStore.currentProject?.id;

        if (!projectId || this.committingAttempt) {
            return null;
        }

        const request = observable.object<RuleImportUploadRequest>({
            projectId,
            origin,
            uploadId,
            onImported,
            phase: 'uploading'
        });

        this.importState = request;
        this.refreshBlockers(request);

        return request;
    }

    isCurrentUpload(uploadId: string) {
        const attempt = this.importState;

        return !!attempt && attempt.uploadId === uploadId && attempt.projectId === this.rulesStore.currentProject?.id;
    }

    @action.bound
    async refreshBlockers(attempt = this.importState) {
        if (!attempt || !this.isCurrentState(attempt)) {
            return false;
        }

        const loaded = await this.rulesStore.getTagsUsageInTestProjects(true).catch(() => false);
        return loaded && this.isCurrentState(attempt);
    }

    @action.bound
    resumeImport() {
        const attempt = this.activeAttempt;

        if (!attempt) {
            return;
        }

        if (this.usageStatus !== 'ready' || this.affectedTestProjects.length > 0) {
            return;
        }

        // The review flow already refreshed usage; this transition only restores the import screen.
        attempt.phase = 'import';
    }

    @action.bound
    async retryImport() {
        const attempt = this.activeAttempt;

        if (!attempt) {
            return;
        }

        attempt.phase = 'import';
        await this.handleImportClick();
    }

    @action.bound
    async setTableData(data: RulesToUploadData, attempt = this.importState, dropIndex?: number) {
        await this.prepareUpload(data, { type: 'rules' }, attempt, dropIndex);
    }

    @action.bound
    async setTableDataForIotaImport(data: RulesToUploadDataForIotaImport, attempt = this.importState) {
        await this.prepareUpload(data.ruleData, { type: 'applications', fileName: data.exportedIotaFileName }, attempt);
    }

    @action.bound
    async setTableDataForProjectFieldsImport(
        data: RulesToUploadDataForProjectBindingsImport,
        attempt = this.importState
    ) {
        await this.prepareUpload(data.ruleData, { type: 'fields', fileName: data.exportedBindingsFileName }, attempt);
    }

    getReplacedTagsUsedInTestProjectsWithNames() {
        return this.replacedTagsUsedInTestProjects.map(usage => {
            const tagName = this.rulesStore.ruleTags.find(tag => tag.id === usage.tagId)?.name ?? usage.tagId;
            return { tagId: usage.tagId, tagName, testProjects: usage.testProjects };
        });
    }

    @action.bound
    async handleImportClick() {
        const attempt = this.activeAttempt;

        if (!attempt || !this.validateImportDraft(attempt)) {
            return;
        }

        const loaded = await this.refreshBlockers(attempt);

        // The selection remains editable while usage loads; validate the current draft before advancing.
        if (!loaded || !this.validateImportDraft(attempt)) {
            return;
        }

        if (this.replacedTagsUsedInTestProjects.length) {
            attempt.phase = 'resolution';
            return;
        }

        if (attempt.step === 'selection') {
            attempt.step = 'mapping';
            return;
        }

        if (attempt.scope === 'full' && this.rulesStore.ruleTags.length) {
            attempt.step = 'confirmation';
        } else {
            await this.importRules();
        }
    }

    @action.bound
    backToMapping() {
        const attempt = this.activeAttempt;

        if (attempt?.step === 'confirmation' && !this.isLoading) {
            attempt.step = 'mapping';
        }
    }

    @action.bound
    async importRules() {
        const attempt = this.activeAttempt;

        if (!attempt || !this.validateImportDraft(attempt)) {
            return;
        }

        const ruleGroupDict = this.getRuleGroupDict(attempt);
        const mappingData = attempt.mapperTableData.map(mapping => ({
            id: mapping.id,
            exportedName: mapping.name,
            nativeName: mapping.bestValue,
            tableType: mapping.tableType
        }));
        const commitAction = this.getCommitAction(attempt.projectId, attempt.generatedFileName, attempt.target);

        this.committingAttempt = attempt;

        try {
            const result = await this.rulesStore.commitImportedRules(
                ruleGroupDict,
                { ...attempt.tagActionDict },
                mappingData,
                commitAction
            );

            if (result.isErr()) {
                if (this.isCurrentState(attempt)) {
                    this.errorStore.addBasicError(new Error(result.error.data?.title ?? result.error.text));
                    const loaded = await this.refreshBlockers(attempt);

                    if (loaded) {
                        attempt.phase = this.replacedTagsUsedInTestProjects.length ? 'resolution' : 'import';
                    }
                }

                return;
            }

            // Release the acknowledged import before fallible refreshes, so they cannot enable replay.
            showImportWarnings(result.value);
            if (this.importState === attempt) {
                this.cancel();
            }

            const refreshes =
                attempt.projectId === this.rulesStore.currentProject?.id
                    ? [
                          this.rulesStore.getRules(),
                          this.rulesStore.getTagsGroups(),
                          this.tagsGroupStore.loadTagsGroups(),
                          this.rulesStore.getProjectTagsVersion()
                      ]
                    : [];

            const refreshed = await Promise.all(
                [...refreshes, Promise.resolve().then(() => attempt.onImported())].map(refresh =>
                    refresh.then(
                        () => true,
                        () => false
                    )
                )
            );

            if (refreshed.includes(false) && attempt.projectId === this.rulesStore.currentProject?.id) {
                this.errorStore.addBasicError(
                    new Error('Rules were imported, but the page could not refresh. Reload the page.')
                );
            }
        } catch (error) {
            if (this.isCurrentState(attempt)) {
                this.errorStore.addBasicError(error instanceof Error ? error : new Error('Could not import rules.'));
            }
        } finally {
            runInAction(() => {
                this.committingAttempt = null;
            });
        }
    }

    @action.bound
    handleBeforeUpload(event?: React.MouseEvent<HTMLElement>) {
        if (!this.rulesStore.tagsGroups.length) {
            event?.stopPropagation();
            message.warning('Please create at least one rule tag group to use import operation');
            return false;
        }

        return !this.isLoading;
    }

    @action.bound
    handleRulesSelection(isChecked: boolean, recordId: string) {
        const attempt = this.activeAttempt;

        if (!attempt || this.isLoading) {
            return;
        }

        attempt.selectedRulesKeys = isChecked
            ? [...attempt.selectedRulesKeys, recordId]
            : attempt.selectedRulesKeys.filter(id => id !== recordId);
    }

    @action.bound
    handleTagsSelection(selectedKeys: string[]) {
        const attempt = this.activeAttempt;

        if (!attempt || this.isLoading) {
            return;
        }

        attempt.selectedTagKeys = selectedKeys;
        attempt.selectedRulesKeysByTag = _.flatten(selectedKeys.map(tag => attempt.importedRules[tag] || [])).map(
            rule => rule.id
        );
        attempt.selectedRulesKeys = attempt.selectedRulesKeys.filter(
            id => !attempt.selectedRulesKeysByTag.includes(id)
        );
    }

    @action.bound
    updateMapperTableData(value: string, name: string) {
        const mapping = this.activeAttempt?.mapperTableData.find(row => row.name === name);

        if (mapping && !this.isLoading) {
            mapping.bestValue = value;
        }
    }

    getGroupIfExists(tagName: string | null) {
        const tag = this.rulesStore.ruleTags.find(candidate => candidate.name === tagName);
        return this.rulesStore.tagsGroups.find(group => group.id === tag?.groupId);
    }

    @action.bound
    handleGroupSelection(tag: string | null, key: string | null) {
        const attempt = this.activeAttempt;

        if (!attempt || this.isLoading) {
            return;
        }

        const tags = tag === null ? Object.keys(attempt.tagGroupDict) : [tag];

        for (const tagName of tags) {
            if (tag !== null || !(this.getGroupIfExists(tagName)?.name || attempt.assignedGroupName)) {
                attempt.tagGroupDict[tagName] = key;
            }
        }
    }

    @action.bound
    handleActionSelection(tag: string, key: 'Add' | 'Replace') {
        const attempt = this.activeAttempt;

        if (attempt && !this.isLoading) {
            attempt.tagActionDict[tag] = key;
        }
    }

    @action.bound
    setExpandedKeys(expanded: boolean, record: TagModel) {
        const attempt = this.activeAttempt;

        if (attempt) {
            attempt.expandedKeys = expanded
                ? [...attempt.expandedKeys, record.name]
                : attempt.expandedKeys.filter(name => name !== record.name);
        }
    }

    @action.bound
    cancelDialog() {
        if (this.isLoading) {
            return;
        }

        if (this.activeAttempt?.step === 'confirmation') {
            this.backToMapping();
            return;
        }

        this.cancel();
    }

    @action.bound
    cancel() {
        this.importState = null;
    }

    dispose() {
        this.cancel();
        this.disposeProjectReaction();
    }

    private async prepareUpload(
        data: RulesToUploadData,
        target: RuleImportCommitTarget,
        state: RuleImportState | null,
        dropIndex?: number
    ) {
        if (!state || !this.isCurrentState(state)) {
            return;
        }

        const assignedGroupName = typeof dropIndex === 'number' ? this.rulesStore.tagsGroups[dropIndex].name : null;
        await this.rulesStore.setRuleTags();

        if (!this.isCurrentState(state)) {
            return;
        }

        runInAction(() => {
            const { projectId: exportedProjectId, importedRules = {}, ...upload } = data;
            const attempt = observable.object<PendingRuleImportAttempt>({
                projectId: state.projectId,
                origin: state.origin,
                uploadId: state.uploadId,
                onImported: state.onImported,
                ...upload,
                importedRules,
                scope: exportedProjectId ? 'full' : 'selective',
                step: exportedProjectId ? 'mapping' : 'selection',
                target,
                phase: 'import',
                assignedGroupName,
                selectedRulesKeys: [],
                selectedTagKeys: [],
                selectedRulesKeysByTag: [],
                tagGroupDict: {},
                tagActionDict: {},
                expandedKeys: [],
                tagsWithDuplicatedRules: [],
                duplicatedRules: []
            });

            for (const tagName of Object.keys(importedRules)) {
                const group = this.getGroupIfExists(tagName);

                attempt.tagGroupDict[tagName] =
                    group?.id ||
                    this.rulesStore.tagsGroups.find(candidate => candidate.name === assignedGroupName)?.id ||
                    null;

                attempt.tagActionDict[tagName] = group ? 'Replace' : 'Add';
            }

            this.importState = attempt;
        });
    }

    private getCommitAction(projectId: string, generatedFileName: string, target: RuleImportCommitTarget) {
        if (target.type === 'rules') {
            return `${endpoints.manageUrl}projects/${projectId}/rules/${generatedFileName}/import/commit`;
        }

        if (target.type === 'applications') {
            const commitUrl =
                `${endpoints.manageUrl}projects/${projectId}/iota/applications/` +
                `${generatedFileName}/${target.fileName}/import/${target.groupId}/commit`;

            return target.rootGroupId ? `${commitUrl}?rootGroupId=${target.rootGroupId}` : commitUrl;
        }

        return `${endpoints.manageUrl}projectFields/${projectId}/${generatedFileName}/${target.fileName}/commit`;
    }

    private get activeAttempt() {
        const attempt = this.pendingAttempt;

        return attempt && this.isCurrentState(attempt) ? attempt : undefined;
    }

    private isCurrentState(state: RuleImportState) {
        return this.importState === state && state.projectId === this.rulesStore.currentProject?.id;
    }

    private getSelectedTags(attempt: PendingRuleImportAttempt) {
        const selectedRuleIds = new Set(this.selectedRuleIds);

        return Object.entries(attempt.importedRules)
            .filter(([, rules]) => rules.some(rule => selectedRuleIds.has(rule.id)))
            .map(([tag]) => tag);
    }

    private getRuleGroupDict(attempt: PendingRuleImportAttempt) {
        const selectedRuleIds = new Set(this.selectedRuleIds);
        const ruleGroupDict = {};

        for (const [tag, rules] of Object.entries(attempt.importedRules)) {
            for (const rule of rules) {
                if (selectedRuleIds.has(rule.id)) {
                    ruleGroupDict[rule.id] = attempt.tagGroupDict[tag];
                }
            }
        }

        return ruleGroupDict;
    }

    private validateImportDraft(attempt: PendingRuleImportAttempt) {
        if (!this.isCurrentState(attempt) || !this.canAdvanceImport) {
            return false;
        }

        if (attempt.scope === 'selective') {
            this.checkForDuplicateNames(attempt);
            return attempt.tagsWithDuplicatedRules.length === 0;
        }

        return true;
    }

    private checkForDuplicateNames(attempt: PendingRuleImportAttempt) {
        const existingNames = this.rulesStore.rules.map(rule => rule.name);
        const selectedIds = this.selectedRuleIds;

        attempt.tagsWithDuplicatedRules = [];
        attempt.duplicatedRules = [];

        for (const [tag, rules] of Object.entries(attempt.importedRules)) {
            if (attempt.tagActionDict[tag] === 'Add' && rules.some(rule => existingNames.includes(rule.name))) {
                const names = rules.filter(rule => selectedIds.includes(rule.id)).map(rule => rule.name);

                if (names.length) {
                    attempt.tagsWithDuplicatedRules.push(tag);
                    attempt.duplicatedRules.push(...names);
                }
            }
        }
    }
}
